import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import requiredIf from 'react-required-if';

import withWidth from '@material-ui/core/withWidth';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import InputAdornment from '@material-ui/core/InputAdornment';

import { Scrollbars } from 'react-custom-scrollbars';

import Progress from 'components/common/Progress';
import IconButton from 'components/common/IconButton';
import IconMagnifier from 'components/icons/IconMagnifier';
import IconCross from 'components/icons/IconCross';
import TextField from 'components/fields/TextField';
import Link from 'components/common/Link';
import IconPlus from 'components/icons/IconPlus';

import isDescendant from 'utils/isDescendant';
import throttle from 'utils/throttle';

import AutosuggestFieldSuggestion from './AutosuggestFieldSuggestion';

const styles = theme => ({
  root: {
    position: 'relative',
  },
  suggestionsContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    minWidth: '100%',
    zIndex: 200,
    borderRadius: 4,
    [theme.breakpoints.down('sm')]: {
      left: '0 !important',
    },
  },
  suggestionsContainerListWidthAuto: {
    right: 'auto',
    minWidth: 130,
  },
  suggestionsContainerListWidthAutoRight: {
    left: 'auto',
    minWidth: 130,
  },
  suggestionsContainerPlacementBottom: {
    top: '100%',
  },
  suggestionsContainerPlacementTop: {
    bottom: '100%',
  },
  iconMagnifier: {
    width: 18,
    height: 18,
    fill: theme.palette.grey[600],
  },
  iconCross: {
    width: 12,
    height: 12,
  },
  buttonClear: {
    margin: '-18px -15px -18px -18px',
  },
  listAdornment: {
    zIndex: 100,
    borderTop: `1px solid ${theme.palette.primary.A100}`,
  },
  listAdornmentLink: {
    padding: '10px 16px',
  },
  iconPlus: {
    width: 14,
    height: 14,
    marginRight: 13,
  },
  textField: {
  },
  suggestion: {
  },
  textFieldInputRoot: {
  },
  textFieldInputInput: {
    textOverflow: 'ellipsis',
  },
  progress: {
    position: 'relative',
    top: 0,
    transform: 'none',
    marginLeft: -12,
    marginRight: 8,
  },
});

class AutosuggestField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      suggestions: [],
      renderSuggestions: props.alwaysRenderSuggestions,
      loadingSuggestions: false,
      offset: 0,
      selectedIndex: -1,
      focused: false,
      suggestionsSearchValue: props.input.value,
      valueBeforeClear: null,
      ...props.onlyConfirmedSelection ? { inputValue: props.input.value } : {},
    };

    this.justSelectedSuggestion = false;
    this.justClickedClearButton = false;
    this.justClickedPaper = false;
    this.justFocused = false;

    this.onSuggestionsFetchRequested =
      throttle(this.onSuggestionsFetchRequested.bind(this), 2000, true, true);
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this);
    this.onScrollSuggestionsFetchRequested = this.onScrollSuggestionsFetchRequested.bind(this);
    this.onSuggestionClick = this.onSuggestionClick.bind(this);
    this.onPaperClick = this.onPaperClick.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClearButtonClick = this.onClearButtonClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onListAdornmentBlur = this.onListAdornmentBlur.bind(this);
  }

  componentDidMount() {
    if (this.props.alwaysRenderSuggestions) {
      this.onSuggestionsFetchRequested();
    }
  }

  componentWillReceiveProps(nextProps) { 
    if (!this.props.disabled && nextProps.disabled) {
      this.setState({
        renderSuggestions: false,
      });
    }
    if (nextProps.input.value !== this.props.input.value
      && nextProps.input.value !== this.state.inputValue) {
      this.setState({
        inputValue: nextProps.input.value,
        ...!this.justSelectedSuggestion ? {
          suggestionsSearchValue: nextProps.input.value,
        } : {},
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { autoHeightMax } = this.props;
    const { offset, suggestionsSearchValue } = this.state;
    if (!this.justSelectedSuggestion) {
      if (suggestionsSearchValue !== prevState.suggestionsSearchValue) {
        this.onSuggestionsFetchRequested();
      } else if (offset > 0 && offset !== prevState.offset) {
        this.onSuggestionsFetchRequested({ push: true });
      }
    }
    if (this.selectedSuggestionRef) {
      const scrollTop = (this.selectedSuggestionRef.offsetTop
        + this.selectedSuggestionRef.getBoundingClientRect().height)
        - autoHeightMax;
      if (scrollTop >= 0) {
        this.scrollbarsRef.scrollTop(scrollTop);
      }
    } else if (this.beforeClearSuggestionRef) {
      const scrollTop = (this.beforeClearSuggestionRef.offsetTop
        + this.beforeClearSuggestionRef.getBoundingClientRect().height)
        - autoHeightMax;
      if (scrollTop >= 0) {
        this.scrollbarsRef.scrollTop(scrollTop);
        this.setState({ valueBeforeClear: null });
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.focusTimeout);
    clearTimeout(this.blurTimeout);
  }

  onScrollSuggestionsFetchRequested() {
    const { limit } = this.props;
    const { suggestions, offset } = this.state;
    if ((suggestions.length - limit) >= offset) {
      this.setState({ offset: offset + limit });
    }
  }

  onSuggestionsFetchRequested(args = { push: false }) {
    const { limit, fetchSuggestions } = this.props;
    const { suggestionsSearchValue } = this.state;
    if (fetchSuggestions && this.inputRef === document.activeElement) {
      this.setState({ loadingSuggestions: true });
      if (args.push) {
        const { suggestions, offset } = this.state;
        fetchSuggestions({ value: suggestionsSearchValue, limit, offset })
          .then((newSuggestions) => {
            if (this.inputRef === document.activeElement
              || (document.body === document.activeElement && this.justClickedClearButton)) {
              this.setState({
                loadingSuggestions: false,
                renderSuggestions: true,
                suggestions: [
                  ...suggestions.slice(0, suggestions.length),
                  ...newSuggestions.slice(0, newSuggestions.length),
                ],
              });
            } else {
              this.setState({ loadingSuggestions: false });
            }
          });
      } else {
        const offset = 0;
        fetchSuggestions({ value: suggestionsSearchValue, limit, offset })
          .then((suggestions) => {
            if (this.inputRef === document.activeElement
              || (document.body === document.activeElement && this.justClickedClearButton)) {
              if (this.scrollbarsRef) {    
                this.scrollbarsRef.scrollTop(0);
              }
              this.setState({
                loadingSuggestions: false,
                renderSuggestions: true,
                suggestions,
                offset,
                selectedIndex: -1,
              });
            } else {
              this.setState({ loadingSuggestions: false });
            }
          });
      }
    }
  }

  onSuggestionsClearRequested() {
    const { alwaysRenderSuggestions } = this.props;
    if (!alwaysRenderSuggestions) {
      this.setState({
        suggestions: [],
      });
    }
  }

  onFocus(event) {
    if (event.target === this.inputRef) {
      this.setState({ focused: true });
      if (!this.justSelectedSuggestion && !this.justClickedPaper) {
        if (!this.justClickedClearButton) {
          this.onSuggestionsFetchRequested();
        }
        this.setState({ renderSuggestions: true });
      }
      this.justClickedClearButton = false;
      this.justFocused = true;
      this.focusTimeout = setTimeout(() => {
        this.justFocused = false;
      }, 200);
      this.props.input.onFocus(event);
    }
  }

  onBlur(event) {
    const { onlyConfirmedSelection } = this.props;
    if (event.target === this.inputRef) {
      this.setState({ focused: false });
      this.blurTimeout = setTimeout(() => {
        const { alwaysRenderSuggestions } = this.props;
        const position = this.inputRef
          ? this.inputRef.value.length
          : 0;
        if (!this.justSelectedSuggestion
          && !this.justClickedClearButton
          && !this.justClickedPaper
          && !this.justFocused) {
          if (!alwaysRenderSuggestions
            && !isDescendant(this.listAdornmentRef, document.activeElement)) {
            this.setState({ renderSuggestions: false });
            this.onSuggestionsClearRequested();
            if (onlyConfirmedSelection) {
              this.props.input.onBlur();
            }
          }
        } else {
          if (this.inputRef) {
            this.inputRef.focus();
            this.inputRef.setSelectionRange(position, position);
          }
          this.justSelectedSuggestion = false;
          this.justClickedClearButton = false;
          this.justClickedPaper = false;
          this.justFocused = false;
        }
        this.blurTimeout = undefined;
      }, 200);
    }
  }

  onListAdornmentBlur() {
    const { onlyConfirmedSelection } = this.props;
    this.blurTimeout = setTimeout(() => {
      const { alwaysRenderSuggestions } = this.props;
      const position = this.inputRef
        ? this.inputRef.value.length
        : 0;
      if (!this.justSelectedSuggestion
        && !this.justClickedClearButton
        && !this.justClickedPaper
        && !this.justFocused) {
        if (!alwaysRenderSuggestions
          && !isDescendant(this.listAdornmentRef, document.activeElement)) {
          this.setState({ renderSuggestions: false });
          this.onSuggestionsClearRequested();
          if (onlyConfirmedSelection) {
            this.props.input.onBlur();
          }
        }
      } else {
        this.inputRef.focus();
        this.inputRef.setSelectionRange(position, position);
        this.justSelectedSuggestion = false;
        this.justClickedClearButton = false;
        this.justClickedPaper = false;
        this.justFocused = false;
      }
      this.blurTimeout = undefined;
    }, 200);
  }

  onKeyDown(event) {
    const {
      input: { onChange, value },
      onSuggestionSelected,
      renderInputContent,
      fetchSuggestions,
      onlyConfirmedSelection,
      keepOpenAfterSelection,
      clearSearchValueAfterSelection,
      ...otherProps
    } = this.props;
    const { selectedIndex, renderSuggestions, loadingSuggestions } = this.state;
    const suggestions = fetchSuggestions ? this.state.suggestions : otherProps.suggestions;
    if (renderSuggestions && suggestions.length > 0) {
      switch (event.key) {
        case 'Enter': {
          if (suggestions[selectedIndex]) {
            const newValue = onSuggestionSelected(suggestions[selectedIndex], value);
            onChange(newValue);
            this.setState({
              ...!keepOpenAfterSelection ? { renderSuggestions: false } : {},
              selectedIndex: -1,
              ...clearSearchValueAfterSelection
                ? { suggestionsSearchValue: '' }
                : { suggestionsSearchValue: newValue },
              ...onlyConfirmedSelection && !clearSearchValueAfterSelection ? {
                inputValue: newValue,
              } : {},
              ...clearSearchValueAfterSelection ? {
                inputValue: '',
              } : {},
            });
            this.justSelectedSuggestion = true;
            this.selectedTimeout = setTimeout(() => {
              this.justSelectedSuggestion = false;
              if (keepOpenAfterSelection) {
                this.onSuggestionsFetchRequested({ push: false });
              }
            }, 200);
          }
          break;
        }
        case 'ArrowDown': {
          const newSelectedIndex = suggestions.length <= selectedIndex + 1
            ? 0 : selectedIndex + 1;
          this.setState({
            selectedIndex: newSelectedIndex,
            ...onlyConfirmedSelection ? {
              inputValue: renderInputContent(suggestions[newSelectedIndex], value),
            } : {},
          });
          if (!onlyConfirmedSelection) {
            onChange(onSuggestionSelected(suggestions[newSelectedIndex], value));
            this.justSelectedSuggestion = true;
            this.selectedTimeout = setTimeout(() => {
              this.justSelectedSuggestion = false;
            }, 200);
          }
          if (newSelectedIndex === 0 && this.scrollbarsRef) {
            this.scrollbarsRef.scrollTop(0);
          }
          break;
        }
        case 'ArrowUp': {
          const newSelectedIndex = selectedIndex <= 0
            ? suggestions.length - 1 : selectedIndex - 1;
          this.setState({
            selectedIndex: newSelectedIndex,
            ...onlyConfirmedSelection ? {
              inputValue: renderInputContent(suggestions[newSelectedIndex], value),
            } : {},
          });
          if (!onlyConfirmedSelection) {
            onChange(onSuggestionSelected(suggestions[newSelectedIndex], value));
            this.justSelectedSuggestion = true;
            this.selectedTimeout = setTimeout(() => {
              this.justSelectedSuggestion = false;
            }, 200);
          }
          break;
        }
        default:
      }
    } else if (event.key === 'ArrowDown' && !loadingSuggestions) {
      this.onSuggestionsFetchRequested();
      this.setState({ renderSuggestions: true });
    }
  }

  onSuggestionClick(item) {
    const {
      input: { onChange, value },
      onSuggestionSelected,
      onlyConfirmedSelection,
      keepOpenAfterSelection,
      clearSearchValueAfterSelection,
    } = this.props;
    const newValue = onSuggestionSelected(item, value);
    onChange(newValue);
    this.setState({
      ...!keepOpenAfterSelection ? { renderSuggestions: false } : {},
      ...clearSearchValueAfterSelection
        ? { suggestionsSearchValue: '' }
        : { suggestionsSearchValue: newValue },
      ...onlyConfirmedSelection && !clearSearchValueAfterSelection ? {
        inputValue: newValue,
      } : {},
      ...clearSearchValueAfterSelection ? {
        inputValue: '',
      } : {},
      selectedIndex: -1,
    });
    this.justSelectedSuggestion = true;
    this.selectedTimeout = setTimeout(() => {
      this.justSelectedSuggestion = false;
      if (keepOpenAfterSelection) {
        this.onSuggestionsFetchRequested({ push: false });
      }
    }, 200);
  }

  onClearButtonClick() {
    const { input, onlyConfirmedSelection, onSearchInputChange } = this.props;
    this.justClickedClearButton = true;
    input.onChange('');
    this.setState({
      suggestionsSearchValue: '',
      ...onlyConfirmedSelection ? { inputValue: '' } : {},
      valueBeforeClear: this.state.suggestionsSearchValue,
    });
    if (!this.blurTimeout) {
      this.inputRef.focus();
    }
    onSearchInputChange('');
    this.clearTimeout = setTimeout(() => {
      this.justClickedClearButton = false;
    }, 200);
  }

  onPaperClick() {
    this.justClickedPaper = true;
  }

  onInputChange(event) {
    const { onSearchInputChange, onlyConfirmedSelection, input } = this.props;
    const newValue = event.target ? event.target.value : event;
    this.setState({ inputValue: newValue });
    onSearchInputChange(newValue);
    if (onlyConfirmedSelection && newValue.length === 0) {
      input.onChange('');
    }
  }

  render() {
    const {
      classes,
      renderSuggestionContent,
      renderTextField,
      onSuggestionSelected,
      autoHeightMax,
      onListAdornmentClick,
      listAdornmentText,
      endAdornmentClearButton,
      textFieldRef,
      fetchSuggestions,
      listWidth,
      disabled,
      suggestionClassName,
      onlyConfirmedSelection,
      listAdornment,
      listAdornmentButtonDisabled,
      suggestionsPlacement,
      width,
      multiselectAutosuggestContainerRef,
      ...otherProps
    } = this.props;
    const {
      renderSuggestions,
      loadingSuggestions,
      selectedIndex,
      inputValue,
      focused,
    } = this.state;
    const suggestions = fetchSuggestions ? this.state.suggestions : otherProps.suggestions;
    const domRect = this.inputRef ? this.inputRef.getBoundingClientRect() : null;
    const multiselectDomRect = multiselectAutosuggestContainerRef
      ? multiselectAutosuggestContainerRef.getBoundingClientRect() : null;
    let placement = 'bottom';
    if (suggestionsPlacement) {
      placement = suggestionsPlacement;
    } else {
      const listAdornmentHeight = onListAdornmentClick || listAdornment ? 40 : 0;
      if (domRect) {
        if ((window.innerHeight - domRect.top - domRect.height)
          < (autoHeightMax + listAdornmentHeight)) {
          placement = 'top';
        }
      }
    }

    return (
      <div className={classes.root}>
        {renderTextField(
          <TextField
            className={classes.textField}
            classes={{
              inputRoot: classes.textFieldInputRoot,
              inputInput: classes.textFieldInputInput,
            }}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onKeyDown={this.onKeyDown}
            inputRef={(inputRef) => {
              this.inputRef = inputRef;
              textFieldRef(inputRef);
            }}
            onClick={(event) => {
              if (this.justFocused && event.target.value) {
                event.target.setSelectionRange(0, event.target.value.length);
              }
            }}
            disabled={disabled}
            {...otherProps}
            {...onlyConfirmedSelection ? {
              input: {
                name: otherProps.input.name,
                value: inputValue,
                onChange: this.onInputChange,
                onFocus: () => {},
                onBlur: () => {},
              } } : {}}
            onChange={(event) => {
              this.setState({ suggestionsSearchValue: event.target.value });
            }}
            {...endAdornmentClearButton ? { endAdornment: (
              <InputAdornment
                position="end"
                className={classes.inputAdorment}
              >
                {loadingSuggestions ? (
                  <Progress
                    size={22}
                    className={classes.progress}
                  />
                ) : (
                  <Fragment>
                    {((onlyConfirmedSelection && inputValue.length > 0)
                      || (!onlyConfirmedSelection && otherProps.input.value.length > 0)) ? (
                        <IconButton
                          className={classes.buttonClear}
                          onClick={this.onClearButtonClick}
                          tooltipContent={!disabled ? 'Clear Value' : null}
                          disabled={disabled}
                          tabIndex={-1}
                        >
                          <IconCross className={classes.iconCross} />
                        </IconButton>
                      ) : (
                        <IconMagnifier className={classes.iconMagnifier} />
                      )}
                  </Fragment>
                )}
              </InputAdornment>
            ) } : {}}
          />,
          focused)}
        {(renderSuggestions
          && (suggestions.length > 0
            || (onListAdornmentClick !== null && !loadingSuggestions)
            || listAdornment)) &&
            <Paper
              onClick={this.onPaperClick}
              className={classNames(classes.suggestionsContainer, {
                [classes.suggestionsContainerPlacementBottom]: placement === 'bottom',
                [classes.suggestionsContainerPlacementTop]: placement === 'top',
                [classes.suggestionsContainerListWidthAuto]: listWidth === 'auto',
                [classes.suggestionsContainerListWidthAutoRight]: listWidth === 'auto-right',
              })}
              {...(width === 'xs') && (domRect || multiselectDomRect) ? {
                style: {
                  left: multiselectDomRect ? `${8 - multiselectDomRect.left}px` : `${8 - domRect.left}px`,
                  width: 'calc(100vw - 16px)',
                },
              } : {}}
            >
              {suggestions.length > 0 &&
                <Scrollbars
                  autoHeight
                  autoHeightMin={0}
                  autoHeightMax={autoHeightMax}
                  thumbMinSize={30}
                  universal
                  ref={(scrollbarsRef) => {
                    this.scrollbarsRef = scrollbarsRef;
                  }}
                  onScrollFrame={(event) => {
                    if (event.top > 0.8) {
                      this.onScrollSuggestionsFetchRequested();
                    }
                  }}
                  renderView={props => (
                    <div {...props} tabIndex="-1" />
                  )}
                >
                  <List disablePadding>
                    {suggestions.map((item, index) => (
                      <AutosuggestFieldSuggestion
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        className={suggestionClassName}
                        onClick={() => this.onSuggestionClick(item)}
                        classes={{
                          root: classes.suggestion,
                        }}
                        {...this.state.valueBeforeClear === renderSuggestionContent(item) ? {
                          rootRef: (beforeClearSuggestionRef) => {
                            if (beforeClearSuggestionRef) {
                              this.beforeClearSuggestionRef = beforeClearSuggestionRef;
                            }
                          },
                        } : {}}
                        {...index === selectedIndex ? {
                          selected: true,
                          rootRef: (selectedSuggestionRef) => {
                            this.selectedSuggestionRef = selectedSuggestionRef;
                          },
                        } : {}}

                      >
                        {renderSuggestionContent(item)}
                      </AutosuggestFieldSuggestion>
                    ))}
                  </List>
                </Scrollbars>
              }
              {(onListAdornmentClick !== null || listAdornment !== null) &&
                <div
                  className={classes.listAdornment}
                  ref={(listAdornmentRef) => {
                    this.listAdornmentRef = listAdornmentRef;
                  }}
                >
                  {onListAdornmentClick !== null &&
                    <Link
                      className={classes.listAdornmentLink}
                      color="primary"
                      bold
                      onClick={() => {
                        if (onlyConfirmedSelection) {
                          this.setState({ inputValue: '' });
                        }
                        this.inputRef.focus();
                        onListAdornmentClick();
                      }}
                      onBlur={this.onListAdornmentBlur}
                      disabled={listAdornmentButtonDisabled}
                    >
                      <IconPlus className={classes.iconPlus} />
                      {listAdornmentText}
                    </Link>
                  }
                  {listAdornment !== null &&
                    listAdornment
                  }
                </div>
              }
            </Paper>
        }
      </div>
    );
  }
}

AutosuggestField.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
  }).isRequired,
  renderSuggestionContent: PropTypes.func,
  renderInputContent: PropTypes.func,
  fetchSuggestions: requiredIf(PropTypes.func, props => !props.suggestions),
  suggestions: requiredIf(PropTypes.arrayOf(PropTypes.object), props => !props.fetchSuggestions),
  onSuggestionSelected: PropTypes.func,
  onListAdornmentClick: PropTypes.func,
  listAdornmentText: PropTypes.string,
  listAdornmentButtonDisabled: PropTypes.bool,
  alwaysRenderSuggestions: PropTypes.bool,
  fullWidth: PropTypes.bool,
  limit: PropTypes.number,
  autoHeightMax: PropTypes.number,
  renderTextField: PropTypes.func,
  endAdornmentClearButton: PropTypes.bool,
  textFieldRef: PropTypes.func,
  listWidth: PropTypes.oneOf(['inherit', 'auto', 'auto-right']),
  disabled: PropTypes.bool,
  suggestionClassName: PropTypes.string,
  onlyConfirmedSelection: PropTypes.bool,
  onSearchInputChange: PropTypes.func,
  keepOpenAfterSelection: PropTypes.bool,
  clearSearchValueAfterSelection: PropTypes.bool,
  listAdornment: PropTypes.node,
  suggestionsPlacement: PropTypes.oneOf(['top', 'bottom']),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    suggestionsContainer: PropTypes.string.isRequired,
    suggestionsContainerListWidthAuto: PropTypes.string.isRequired,
    suggestionsContainerPlacementBottom: PropTypes.string.isRequired,
    suggestionsContainerPlacementTop: PropTypes.string.isRequired,
    iconCross: PropTypes.string.isRequired,
    buttonClear: PropTypes.string.isRequired,
    iconPlus: PropTypes.string.isRequired,
    textField: PropTypes.string,
    suggestion: PropTypes.string,
  }).isRequired,
};

AutosuggestField.defaultProps = {
  renderSuggestionContent: suggestion => suggestion,
  renderInputContent: suggestion => suggestion,
  onSuggestionSelected: suggestion => suggestion,
  renderTextField: textField => textField,
  textFieldRef: () => {},
  onSearchInputChange: () => {},
  alwaysRenderSuggestions: false,
  endAdornmentClearButton: true,
  onListAdornmentClick: null,
  listAdornmentText: 'Add New',
  fullWidth: false,
  limit: 10,
  autoHeightMax: 200,
  fetchSuggestions: null,
  suggestions: null,
  disabled: false,
  listWidth: 'inherit',
  suggestionClassName: '',
  onlyConfirmedSelection: false,
  keepOpenAfterSelection: false,
  clearSearchValueAfterSelection: false,
  listAdornment: null,
  listAdornmentButtonDisabled: false,
  suggestionsPlacement: null,
};

export default withStyles(styles)(withWidth()(AutosuggestField));



// WEBPACK FOOTER //
// ./src/components/fields/AutosuggestField/AutosuggestField.js